import React, { useEffect, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { bapi, usd } from '@/lib/billingApi';
import { Sel } from './shared';

const methodsFor = (p) => {
  const card = p.gateway === 'plugnpay' && !!p.order_id && !(p.refunds || []).some((r) => r.method?.startsWith('gateway_'));
  return [
    card && ['gateway_refund', "Refund to card (Plug'n Pay return)"],
    card && p.status === 'paid' && ['gateway_void', 'Void (same day, full amount, before settlement)'],
    ['external', 'Record a refund made outside (PayPal / portal)'],
  ].filter(Boolean);
};

export const RefundDialog = ({ open, p, onClose, onDone }) => {
  const remaining = Math.round((p.total - (p.refunded_amount || 0)) * 100) / 100;
  const methods = methodsFor(p);
  const [f, setF] = useState({});
  const [busy, setBusy] = useState(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { if (open) setF({ amount: remaining.toFixed(2), method: methods[0][0], reason: '', reference: '', revoke: false }); }, [open]);
  const set = (k) => (v) => setF((x) => ({ ...x, [k]: v, ...(k === 'method' && v === 'gateway_void' ? { amount: p.total.toFixed(2) } : {}) }));
  const external = f.method === 'external';
  const valid = Number(f.amount) > 0 && Number(f.amount) <= remaining && f.reason?.trim() && (!external || f.reference?.trim());

  const submit = async () => {
    setBusy(true);
    try {
      await bapi(`/admin/billing/transactions/${p.id}/refund`, { method: 'POST', body: {
        amount: Number(f.amount), method: f.method, reason: f.reason.trim(), reference: f.reference.trim(), revoke_access: f.revoke } });
      toast.success(`Refund of ${usd(f.amount)} recorded`);
      onDone();
    } catch (e) { toast.error(e.message); } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!o && !busy) onClose(); }}>
      <DialogContent className="sm:max-w-md" data-testid="refund-dialog">
        <DialogHeader>
          <DialogTitle>Refund {p.user_email}</DialogTitle>
          <DialogDescription>Paid {usd(p.total)} · up to {usd(remaining)} can still be refunded.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div><Label className="text-xs">Method</Label><Sel value={f.method || ''} onChange={set('method')} options={methods} testId="refund-method-select" className="w-full block" /></div>
          <div><Label className="text-xs">Amount (USD)</Label><Input type="number" step="0.01" min="0.01" max={remaining} value={f.amount || ''} disabled={f.method === 'gateway_void'} onChange={(e) => set('amount')(e.target.value)} data-testid="refund-amount-input" /></div>
          <div><Label className="text-xs">Reason</Label><Textarea rows={2} value={f.reason || ''} onChange={(e) => set('reason')(e.target.value)} placeholder="Shown in the audit trail" data-testid="refund-reason-input" /></div>
          {external && <div><Label className="text-xs">External reference</Label><Input value={f.reference || ''} onChange={(e) => set('reference')(e.target.value)} placeholder="PayPal / portal transaction id" data-testid="refund-reference-input" /></div>}
          {p.subscription_id && (
            <label className="flex items-center gap-2 text-sm text-slate-700 cursor-pointer" htmlFor="refund-revoke">
              <Checkbox id="refund-revoke" checked={!!f.revoke} onCheckedChange={(v) => set('revoke')(!!v)} data-testid="refund-revoke-checkbox" />End the customer's access now
            </label>
          )}
        </div>
        <DialogFooter className="gap-2">
          <Button variant="ghost" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={submit} disabled={busy || !valid} className="bg-violet-600 hover:bg-violet-700" data-testid="refund-submit-btn">
            {busy && <Loader2 className="w-4 h-4 mr-1.5 animate-spin" />}Refund {usd(f.amount)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
